import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { CookieSet, RoleTitleGroup } from "../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import {
  RoleTitleCookieState,
  getCheckboxStateFromCookieState,
  getRoleKey,
  getSelectedAdLibIds,
} from "./roleTitleSetUtils";

type Options = {
  /** Fired with the next selection whenever the user toggles or clears items. */
  onChange?: (next: RoleTitleCookieState) => void;
};

/**
 * Shared selection mechanics for the roles & titles picker. Owns the selected
 * `cookieSets` and derives everything else (`checkBoxes`, `selectedCount`) from
 * it, so callers never store redundant state. Consumer-specific side-effects
 * (e.g. dispatching to Redux) are wired via `onChange`.
 */
export const useRoleTitleSelection = (
  initial: RoleTitleCookieState | (() => RoleTitleCookieState) = {},
  opts?: Options,
) => {
  const roleTitleList = useSelector((state: ReduxApplicationState) =>
    state.campaignToCreate.roleTitleList.filter((g) => g.roleType !== "Companies"),
  );
  const [cookieSets, setCookieSets] = useState<RoleTitleCookieState>(initial);
  const cookieSetsRef = useRef(cookieSets);
  cookieSetsRef.current = cookieSets;
  const onChange = opts?.onChange;

  const checkBoxes = useMemo(
    () => getCheckboxStateFromCookieState(roleTitleList, cookieSets),
    [roleTitleList, cookieSets],
  );
  const selectedCount = useMemo(() => getSelectedAdLibIds(cookieSets).length, [cookieSets]);

  const handleChange = useCallback(
    (key: string, cookieSet: CookieSet[]) => {
      const prev = cookieSetsRef.current;
      let next: RoleTitleCookieState;
      if (prev[key]?.length) {
        const { [key]: _omit, ...rest } = prev;
        next = rest;
      } else {
        next = { ...prev, [key]: cookieSet };
      }
      setCookieSets(next);
      onChange?.(next);
    },
    [onChange],
  );

  // Select/deselect every role in a group at once (one update → one onChange).
  const toggleGroup = useCallback(
    (group: RoleTitleGroup, selectAll: boolean) => {
      const next = { ...cookieSetsRef.current };
      group.roleSet.forEach((rs) => {
        const key = getRoleKey(group.roleTypeId, rs.roleId);
        if (selectAll) next[key] = rs.cookieSets;
        else delete next[key];
      });
      setCookieSets(next);
      onChange?.(next);
    },
    [onChange],
  );

  const clearAll = useCallback(() => {
    setCookieSets({});
    onChange?.({});
  }, [onChange]);

  // Replaces the selection without firing onChange (used when the source of
  // truth is set elsewhere, e.g. picking a pre-made set that dispatches itself).
  const reset = useCallback((next: RoleTitleCookieState) => setCookieSets(next), []);

  return { cookieSets, checkBoxes, selectedCount, handleChange, toggleGroup, clearAll, reset };
};
